/**
 * ThemeManager - Thin wrapper around React ThemeState
 *
 * This ThemeManager provides a singleton interface for non-React code to access
 * theme state. It delegates all operations to the React ThemeStateProvider.
 *
 * All theme logic and data is defined in theme-state.tsx (single source of truth).
 * This file is just a read-only interface wrapper.
 *
 * IMPORTANT: ThemeStateProvider must be mounted before accessing ThemeManager,
 * otherwise getReactThemeState() will throw an error.
 */

import {
  type AvailableDomains,
  getReactThemeState,
  type IntercomApp,
  type Theme,
  type ThemeSettings,
} from '../../react/state/theme-state';
import { CerbosFeature } from '../../services/cerbos/features';

// Re-export types for external use
export type { AvailableDomains, IntercomApp, Theme, ThemeSettings };

export class ThemeManager {
  protected static _instance = new ThemeManager();

  static get instance() {
    return this._instance;
  }

  get theme(): Theme {
    return getReactThemeState().theme;
  }

  get isSharedView(): boolean {
    return getReactThemeState().isSharedView;
  }

  get settings(): ThemeSettings {
    return getReactThemeState().settings;
  }

  reinitialize() {
    // Theme initialization is handled by ThemeStateProvider
    // This method is kept for API compatibility but does nothing
  }

  initSettings(isSharedView: boolean): void {
    getReactThemeState().setSharedView(isSharedView);
  }

  isSwedishDomain(): boolean {
    return getReactThemeState().isSwedishDomain();
  }

  isMobaTheme(): boolean {
    return getReactThemeState().isMobaTheme();
  }

  isMakinTheme(): boolean {
    return getReactThemeState().isMakinTheme();
  }

  getThemeName(): 'default' | 'moba' | 'makin' {
    return getReactThemeState().getThemeName();
  }

  getTermsOfServiceUrl(): string {
    return getReactThemeState().getTermsOfServiceUrl();
  }

  isSwedishLanguageSelected(): boolean {
    return getReactThemeState().isSwedishLanguageSelected();
  }

  getCloudProcessingTermsOfServiceUrl(): string {
    return getReactThemeState().getCloudProcessingTermsOfServiceUrl();
  }

  getOnDemandPriceListUrl(): string {
    return getReactThemeState().getOnDemandPriceListUrl();
  }

  getHelpCenterUrl(): string {
    return getReactThemeState().getHelpCenterUrl();
  }

  getLogoTypeUrl(): string {
    return getReactThemeState().getLogoTypeUrl();
  }

  getHostname(): AvailableDomains {
    return getReactThemeState().getHostname();
  }

  getProductName(): string {
    return getReactThemeState().getProductName();
  }

  getIntercomApp(): IntercomApp {
    return getReactThemeState().getIntercomApp();
  }

  hostnameStartsWith(value: string): boolean {
    return getReactThemeState().hostnameStartsWith(value);
  }

  hasAccessToRoute(routePath: string): boolean {
    return getReactThemeState().hasAccessToRoute(routePath);
  }

  hasAccessToFeature(cerbosKey: CerbosFeature): boolean {
    return getReactThemeState().hasAccessToFeature(cerbosKey);
  }
}
